import styled, { css } from 'styled-components';

import { Flex } from '@/components/Flex';
import { theme } from '@/theme';

import type { ErrorVariant, InputProps } from './Input';

export const getInputBackgroundColor = (
  variant: InputProps['colorVariant'],
) => {
  switch (variant) {
    case 'white': {
      return theme.colors.white;
    }
    case 'grey': {
      return theme.colors.lightGrey;
    }
    case 'transparent': {
      return 'transparent';
    }
    case 'primaryLight': {
      return theme.colors.primaryLight;
    }
    case 'secondaryLightGrey': {
      return theme.colors.secondaryLightGrey;
    }
    default: {
      return theme.colors.white;
    }
  }
};

export const getInputBorderColor = (
  variant: InputProps['borderColorVariant'],
) => {
  switch (variant) {
    case 'lightGray': {
      return theme.colors.lightGray;
    }
    default: {
      return theme.colors.secondaryGrey;
    }
  }
};

export const Wrapper = styled(Flex)<{
  $variant?: InputProps['variant'];
  colorVariant: InputProps['colorVariant'];
}>`
  width: 100%;
  position: relative;
  background: ${({ colorVariant }) => getInputBackgroundColor(colorVariant)};

  ${({ $variant, theme }) =>
    $variant === 'boxed' &&
    css`
      border: ${theme.components.input.variant.boxed.borderWidth}px solid
        ${theme.colors.secondaryGrey};
    `}

  ${({ $variant, theme }) =>
    $variant === 'boxedBold' &&
    css`
      border: ${theme.components.input.variant.boxedBold.borderWidth}px solid
        ${theme.colors.primaryBlue};
      height: ${theme.components.select.variant.boxed.height};

      input {
        font-weight: bold;
        text-transform: uppercase;
        color: ${theme.colors.primaryBlue};
      }
    `}
`;

export const InputContainer = styled(Flex)<{ $hasPrefix: boolean }>`
  position: relative;
  width: 100%;

  ${({ $hasPrefix, theme }) =>
    $hasPrefix &&
    css`
      & > .prefix {
        font-size: ${theme.fontSizes.p2};
        position: absolute;
        left: 10px;
        top: calc(50% - 0.5px); //little offset to align with the input
        transform: translateY(-50%);
        z-index: 1;
      }

      input {
        padding-left: 42px;
      }
    `}
`;

export const Input = styled.input<
  Pick<InputProps, 'borderColorVariant'> & {
    $errorVariant: ErrorVariant;
    $variant?: InputProps['variant'];
    error: boolean;
  }
>`
  border: none;
  background: transparent;
  z-index: 1;
  width: 100%;
  /* iOS Safari auto-zooms inputs with font-size < 16px on focus; keep 16px on mobile, revert to design token at md+ */
  font-size: 16px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      font-size: ${theme.fontSizes.p2};
    }
  `}

  ${({ $variant, borderColorVariant, error, theme }) => css`
    padding: ${theme.spacing(2, 0, 0.5, 1)};
    ${$variant !== 'boxed' &&
    $variant !== 'boxedBold' &&
    css`
      border-bottom: 1px solid
        ${error ? theme.colors.error : getInputBorderColor(borderColorVariant)};
    `}
  `}

  ${({ $errorVariant, error, theme }) =>
    error &&
    $errorVariant === 'full' &&
    css`
      color: ${theme.colors.error};
      border: 1px solid ${theme.colors.error};
    `}

  &:not(:placeholder-shown) + label,
  &:focus + label {
    top: 26%;
    font-size: ${({ theme }) => theme.fontSizes.p3};
  }

  &:focus {
    outline: 1px solid transparent;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      &:hover {
        cursor: not-allowed;
      }
    `}

  /**
   * Disable increase/decrease arrows for number input
   */
  ${({ type }) =>
    type === 'number' &&
    css`
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type='number'] {
        -moz-appearance: textfield;
      }
    `}

  /**
   * Below styles prevent unwanted styling of input when data is autocompleted
   */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-background-clip: text;
  }
`;

export const InputNoLabel = styled(Input)`
  ${({ theme }) => css`
    padding: ${theme.spacing(1.2, 0, 1.2, 1)};
  `}
`;

export const Label = styled.label`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: 0.2s;
  color: ${theme.colors.secondaryGrey};

  ${({ theme }) => css`
    left: ${theme.spacing(1)};
    font-size: ${theme.fontSizes.p2};
  `}
`;

export const Prefix = styled.span<{ $error?: boolean; $hasLabel?: boolean }>`
  ${({ $error, theme }) =>
    $error &&
    css`
      color: ${theme.colors.error};
    `}

  ${({ $hasLabel, theme }) =>
    $hasLabel &&
    css`
      margin-top: ${theme.spacing(0.8)};
    `}
`;
